import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth-context'
import { useState } from 'react'
import { SUPPORT_READ_ONLY_EXPLANATION, useSupportReadOnlyView } from '../lib/support-session'

export default function Nav() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchQ, setSearchQ] = useState('')
  const { supportSession } = useSupportReadOnlyView()

  if (!user) return null

  const accountType = String(user.accountType ?? '').toUpperCase()
  const userRoles = new Set((user.roles ?? []).map((role) => String(role).toUpperCase()))
  const isInternal = ['ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'OWNER'].includes(accountType)
  const isDealer = userRoles.has('DEALER') || userRoles.has('SHIPPER') || ['DEALER', 'SHIPPER'].includes(accountType)
  const isBroker = !isInternal && ['BROKER_ADMIN', 'BROKER', 'BROKER_USER'].includes(accountType)
  const isCarrier = !isInternal && (['CARRIER_ADMIN', 'CARRIER_OPERATIONS'].includes(accountType) || (accountType === 'DISPATCHER' && !isDealer))
  const isAccounting = !isInternal && accountType === 'ACCOUNTING'
  const isDriver = !isInternal && accountType === 'DRIVER'
  const isCustomer = accountType === 'CUSTOMER'

  const active = (path: string) =>
    location.pathname.startsWith(path) ? 'text-white font-semibold' : 'text-blue-200 hover:text-white'

  const signOut = () => {
    logout()
    navigate('/login')
  }

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const q = searchQ.trim()
    if (!q) return
    navigate(`/search?q=${encodeURIComponent(q)}`)
  }

  const dispatcherNav = [
    { to: '/dispatcher', label: 'Today' },
    { to: '/quote-desk', label: 'Quotes' },
    { to: '/orders', label: 'Orders' },
    { to: '/loads', label: 'Trips' },
    { to: '/dispatch-board', label: 'Issues' },
  ]

  const carrierNav = [
    { to: '/carrier/loads', label: 'Assigned Trips' },
    { to: '/driver/loads/11111111-1111-4111-8111-111111112205/load-plan', label: 'Load Plans' },
    { to: '/carrier/loads?focus=issues', label: 'Issues' },
  ]

  const driverNav = [
    { to: '/driver/app', label: 'Today' },
    { to: '/driver/jobs', label: 'My Trip' },
    { to: '/driver/jobs?focus=inspections', label: 'Inspections' },
  ]

  const customerNav = [
    { to: '/q/wahatoya-auto-group', label: 'Get a Quote' },
    { to: '/quote-response/wahatoya-ready-order-token', label: 'My Quotes' },
    { to: '/customer-portal/wahatoya-ready-order-token', label: 'My Orders' },
    { to: '/customer-portal/wahatoya-eastbound-token', label: 'Track Vehicle' },
    { to: '/customer-portal/wahatoya-ready-order-token#documents', label: 'Documents' },
  ]

  const adminBusinessNav = [
    { to: '/dispatcher', label: 'Command Center' },
    { to: '/quote-desk', label: 'Quotes & Intake' },
    { to: '/orders', label: 'Orders' },
    { to: '/accounting', label: 'Accounting' },
    { to: '/invoices', label: 'Invoices' },
  ]

  const adminMoveNav = [
    { to: '/loads', label: 'Trips & Loads' },
    { to: '/dispatch-board', label: 'Dispatch Board' },
    { to: '/carriers', label: 'Carriers' },
    { to: '/carrier/drivers', label: 'Drivers' },
    { to: '/carrier/equipment', label: 'Equipment' },
  ]

  const adminCustomersNav = [
    { to: '/customers', label: 'Accounts' },
    { to: '/places', label: 'Locations' },
    { to: '/lanes', label: 'Shipping Lanes' },
    { to: '/vin-inventory', label: 'VIN Inventory' },
  ]

  const adminNav = [
    { to: '/admin', label: 'Admin Home' },
    { to: '/settings', label: 'Settings' },
    { to: '/imports', label: 'Imports' },
    { to: '/demo-status', label: 'Demo Status' },
    { to: '/search', label: 'Search' },
  ]

  return (
    <div>
      {supportSession?.sessionId && (
        <div className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          <span className="font-semibold uppercase tracking-wide">Support View Active</span>
          <span className="mx-2">•</span>
          <span>Tenant {supportSession.targetTenantOrgId ?? 'N/A'}</span>
          <span className="mx-2">•</span>
          <span>{supportSession.reason ?? 'No reason provided'}</span>
          <span className="mx-2">•</span>
          <span>{SUPPORT_READ_ONLY_EXPLANATION}</span>
        </div>
      )}
      <div className="lg:flex">
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:min-h-screen lg:bg-blue-800 lg:text-sm lg:py-6 lg:px-4">
        <div className="flex items-center gap-3 mb-6">
          <span className="text-white font-bold text-lg">Diamond Dispatch</span>
          <span className="text-blue-300 text-xs uppercase tracking-wide">Golden Demo</span>
        </div>

        <nav className="flex-1 space-y-6">
          {(isBroker || isInternal) && (
            <div className="space-y-2">
              <div className="text-xs text-blue-200 uppercase tracking-wide">Dispatcher</div>
              {dispatcherNav.map((item) => (
                <Link key={item.label} to={item.to} className={`block ${active(item.to)}`}>{item.label}</Link>
              ))}
            </div>
          )}

          {isCarrier && (
            <div className="space-y-2">
              <div className="text-xs text-blue-200 uppercase tracking-wide">Carrier</div>
              {carrierNav.map((item) => (
                <Link key={item.label} to={item.to} className={`block ${active(item.to)}`}>{item.label}</Link>
              ))}
            </div>
          )}

          {isDriver && (
            <div className="space-y-2">
              <div className="text-xs text-blue-200 uppercase tracking-wide">Driver</div>
              {driverNav.map((item) => (
                <Link key={item.label} to={item.to} className={`block ${active(item.to)}`}>{item.label}</Link>
              ))}
            </div>
          )}

          {isCustomer && (
            <div className="space-y-2">
              <div className="text-xs text-blue-200 uppercase tracking-wide">Customer</div>
              {customerNav.map((item) => (
                <Link key={item.label} to={item.to} className={`block ${active(item.to)}`}>{item.label}</Link>
              ))}
            </div>
          )}

          {isInternal && (
            <>
              <div className="space-y-2">
                <div className="text-xs text-blue-200 uppercase tracking-wide">Run the Business</div>
                {adminBusinessNav.map((item) => (
                  <Link key={item.label} to={item.to} className={`block ${active(item.to)}`}>{item.label}</Link>
                ))}
              </div>
              <div className="space-y-2">
                <div className="text-xs text-blue-200 uppercase tracking-wide">Move Vehicles</div>
                {adminMoveNav.map((item) => (
                  <Link key={item.label} to={item.to} className={`block ${active(item.to)}`}>{item.label}</Link>
                ))}
              </div>
              <div className="space-y-2">
                <div className="text-xs text-blue-200 uppercase tracking-wide">Manage Customers</div>
                {adminCustomersNav.map((item) => (
                  <Link key={item.label} to={item.to} className={`block ${active(item.to)}`}>{item.label}</Link>
                ))}
              </div>
              <div className="space-y-2">
                <div className="text-xs text-blue-200 uppercase tracking-wide">Administration</div>
                {adminNav.map((item) => (
                  <Link key={item.label} to={item.to} className={`block ${active(item.to)}`}>{item.label}</Link>
                ))}
              </div>
            </>
          )}

          {isAccounting && (
            <div className="space-y-2">
              <div className="text-xs text-blue-200 uppercase tracking-wide">Accounting</div>
              <Link to="/accounting" className={`block ${active('/accounting')}`}>Today</Link>
              <Link to="/invoices" className={`block ${active('/invoices')}`}>Invoices</Link>
            </div>
          )}
        </nav>

        <div className="mt-6 pt-4 border-t border-blue-700 text-sm text-blue-200">
          <div className="mb-2">{user.name ?? user.email} · {user.accountType}</div>
          <button onClick={signOut} className="text-xs bg-blue-700 hover:bg-blue-600 text-white px-3 py-1 rounded">Sign out</button>
        </div>
      </aside>

      <nav className="lg:hidden bg-blue-800 px-4 py-3 flex items-center justify-between gap-4 text-sm">
        <span className="text-white font-bold">Diamond Dispatch</span>
        <div className="flex items-center gap-2">
          <form onSubmit={onSearch} className="flex items-center gap-1">
            <input
              className="w-40 rounded border border-blue-700 bg-blue-900 px-2 py-1 text-xs text-white placeholder-blue-300"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search"
            />
          </form>
          <button onClick={signOut} className="text-xs bg-blue-700 hover:bg-blue-600 text-white px-3 py-1 rounded">Sign out</button>
        </div>
      </nav>
      </div>
    </div>
  )
}
